import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import ListingCard from '../components/ListingCard';
import PropertyMap from '../components/PropertyMap';
import { generateListingExplanation } from '../services/api';
import { getFavorites, addFavorite, removeFavorite, getLifestyleProfile, generateComparison, saveSearch, getLifestyleToggle } from '../services/authService';

export default function ResultsPage() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [results, setResults] = useState<any>(null);
  const [profile, setProfile] = useState<any>(null);
  const [customPois, setCustomPois] = useState<Array<{ label: string; address: string; lat: number; lng: number }>>([]);
  const [activeTab, setActiveTab] = useState<'list' | 'map'>('list');
  const [explanations, setExplanations] = useState<Map<number, string>>(new Map());
  const [loadingExplanations, setLoadingExplanations] = useState<Set<number>>(new Set());
  const [showSaveBanner, setShowSaveBanner] = useState(true);
  const [hoveredListingId, setHoveredListingId] = useState<number | null>(null);
  const [favoriteIds, setFavoriteIds] = useState<Set<number>>(new Set());
  const [highlightedListingId, setHighlightedListingId] = useState<number | null>(null);
  const [focusedListingId, setFocusedListingId] = useState<number | null>(null);
  const listingCardsRef = useRef<Record<number, HTMLDivElement | null>>({});

  // Comparison state
  const [compareIds, setCompareIds] = useState<Set<number>>(new Set());
  const [isComparing, setIsComparing] = useState(false);
  const [comparisonResult, setComparisonResult] = useState<string | null>(null);
  const [showComparisonModal, setShowComparisonModal] = useState(false);
  const [showCompareInfo, setShowCompareInfo] = useState(false);

  useEffect(() => {
    const stored = sessionStorage.getItem('searchResults');
    const storedProfile = sessionStorage.getItem('searchProfile');
    if (!stored) {
      navigate('/');
      return;
    }
    try {
      setResults(JSON.parse(stored));
      if (storedProfile) {
        setProfile(JSON.parse(storedProfile));
      }
    } catch {
      navigate('/');
    }
  }, []);

  // Load user's custom POIs from lifestyle profile
  useEffect(() => {
    if (!isAuthenticated) return;
    getLifestyleProfile()
      .then(data => {
        const pois = data.profile?.places?.custom_pois;
        if (Array.isArray(pois) && pois.length > 0) {
          setCustomPois(pois);
        }
      })
      .catch(() => {}); // No profile yet or error – ignore
  }, [isAuthenticated]);

  // Load user's favorites on mount
  useEffect(() => {
    if (!isAuthenticated) return;
    async function loadFavorites() {
      try {
        const favs = await getFavorites();
        const ids = new Set<number>(favs.map((f: any) => f.property_id));
        setFavoriteIds(ids);
      } catch {
        // Not logged in or error - silently ignore
      }
    }
    loadFavorites();
  }, [isAuthenticated]);

  // Auto-save search when user logs in and search wasn't saved yet
  useEffect(() => {
    if (!isAuthenticated) return;
    const alreadySaved = sessionStorage.getItem('currentSearchId');
    if (alreadySaved) return;
    const storedProfile = sessionStorage.getItem('searchProfile');
    const storedMessages = sessionStorage.getItem('chatMessages');
    const storedResults = sessionStorage.getItem('searchResults');
    if (!storedProfile || !storedResults) return;

    async function autoSave() {
      try {
        const profileData = JSON.parse(storedProfile!);
        const msgs = storedMessages ? JSON.parse(storedMessages) : [];
        const resultsData = JSON.parse(storedResults!);
        const hard = profileData?.hard || {};
        const profileName = [hard.preferred_canton, hard.preferred_city].filter(Boolean).join(' / ') || 'Meine Suche';
        const allResults = [...(resultsData.topMatches || []), ...(resultsData.nearMisses || [])];

        const saved = await saveSearch({
          profileName,
          chatMessages: msgs,
          profile: profileData,
          results: allResults,
          aiProvider: 'openai',
          lifestyleEnabled: getLifestyleToggle(),
        });
        if (saved.search?.id) {
          sessionStorage.setItem('currentSearchId', saved.search.id.toString());
        }
        console.log('[Results] Auto-saved search after login:', saved.search?.id);
      } catch (err) {
        console.error('[Results] Auto-save after login failed:', err);
      }
    }
    autoSave();
  }, [isAuthenticated]);

  const handleToggleFavorite = useCallback(async (listingId: number) => {
    if (!isAuthenticated) {
      // Prompt user to login
      if (confirm('Bitte melden Sie sich an, um Favoriten zu speichern. Jetzt anmelden?')) {
        navigate('/#lab-auth');
      }
      return;
    }

    const isFav = favoriteIds.has(listingId);
    
    // Optimistic update
    setFavoriteIds(prev => {
      const next = new Set(prev);
      if (isFav) {
        next.delete(listingId);
      } else {
        next.add(listingId);
      }
      return next;
    });

    try {
      if (isFav) {
        await removeFavorite(listingId);
      } else {
        await addFavorite(listingId);
      }
    } catch {
      // Revert on error
      setFavoriteIds(prev => {
        const next = new Set(prev);
        if (isFav) {
          next.add(listingId);
        } else {
          next.delete(listingId);
        }
        return next;
      });
    }
  }, [isAuthenticated, favoriteIds, navigate]);

  const topMatches = results?.topMatches;
  const nearMisses = results?.nearMisses;
  const stats = results?.stats;
  const commuteOverlap = results?.commuteOverlap;
  const allListings = useMemo(
    () => [...(topMatches || []), ...(nearMisses || [])],
    [topMatches, nearMisses]
  );

  const handleToggleCompare = useCallback((listingId: number) => {
    setCompareIds(prev => {
      const next = new Set(prev);
      if (next.has(listingId)) {
        next.delete(listingId);
      } else if (next.size < 3) {
        next.add(listingId);
      }
      return next;
    });
  }, []);

  const handleStartComparison = useCallback(async () => {
    if (compareIds.size < 2 || !profile) return;
    if (!isAuthenticated) {
      if (confirm('Bitte melden Sie sich an, um Objekte zu vergleichen. Jetzt anmelden?')) {
        navigate('/#lab-auth');
      }
      return;
    }

    setIsComparing(true);
    setShowComparisonModal(true);
    setComparisonResult(null);

    try {
      const selectedListings = allListings.filter(l => compareIds.has(l.id));
      
      // Fetch lifestyle profile for additional context (includes summary)
      let lifestyleProfile = null;
      try {
        const lp = await getLifestyleProfile();
        lifestyleProfile = lp.profile ? { ...lp.profile, summary: lp.summary } : null;
      } catch { /* no profile yet */ }

      // Merge custom POIs into profile
      const profileWithPois = {
        ...profile,
        places: {
          ...profile.places,
          custom_pois: customPois.length > 0 ? customPois : (profile.places?.custom_pois || []),
        },
      };

      const result = await generateComparison({
        listings: selectedListings,
        profile: profileWithPois,
        lifestyleProfile,
      });

      setComparisonResult(result.comparison);
    } catch (err: any) {
      console.error('Comparison error:', err);
      setComparisonResult('Vergleich konnte nicht generiert werden. Bitte versuchen Sie es erneut.');
    } finally {
      setIsComparing(false);
    }
  }, [compareIds, profile, isAuthenticated, allListings, customPois, navigate]);

  const handleCardClick = useCallback((listingId: number) => {
    // On mobile, switch to map tab to show the focused marker
    setActiveTab('map');
    // Toggle focus: if clicking the same card again, reset and re-trigger
    setFocusedListingId(null);
    setTimeout(() => setFocusedListingId(listingId), 50);
  }, []);

  const handleMarkerClick = useCallback((listingId: number) => {
    // On mobile, switch to list tab so the card is visible
    setActiveTab('list');

    // Scroll to the listing card
    const el = listingCardsRef.current[listingId];
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    // Highlight the listing card (stays until popup is closed)
    setHighlightedListingId(listingId);
  }, []);

  const handlePopupClose = useCallback(() => {
    setHighlightedListingId(null);
  }, []);

  async function handleRequestExplanation(listing: any) {
    if (!profile) return;
    
    setLoadingExplanations(prev => new Set(prev).add(listing.id));
    
    try {
      // Merge custom POIs into profile so the AI can mention nearby personal places
      const profileWithPois = {
        ...profile,
        places: {
          ...profile.places,
          custom_pois: customPois.length > 0 ? customPois : (profile.places?.custom_pois || []),
        },
      };
      const result = await generateListingExplanation(listing.id, listing, profileWithPois);
      setExplanations(prev => new Map(prev).set(listing.id, result.explanation));
    } catch (err: any) {
      console.error('Explanation error:', err);
      setExplanations(prev => new Map(prev).set(listing.id, 'Erklärung konnte nicht generiert werden.'));
    } finally {
      setLoadingExplanations(prev => {
        const next = new Set(prev);
        next.delete(listing.id);
        return next;
      });
    }
  }

  if (!results) {
    return (
      <div className="loading-page">
        <div className="spinner" />
        <p>Ergebnisse werden geladen...</p>
      </div>
    );
  }

  return (
    <div className="results-page">
      {/* Save Search Banner (for anonymous users) */}
      {!isAuthenticated && showSaveBanner && (
        <div className="save-banner">
          <div className="save-banner-content">
            <div className="save-banner-icon"><svg viewBox="0 0 24 24" width="32" height="32" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg></div>
            <div className="save-banner-text">
              <h3>Suche speichern?</h3>
              <p>Registrieren Sie sich kostenlos, um diese Suche zu speichern, Favoriten zu markieren und den Markt zu beobachten.</p>
            </div>
            <div className="save-banner-actions">
              <Link to="/#lab-auth" className="btn btn--primary">Jetzt registrieren</Link>
              <Link to="/#lab-auth" className="btn btn--secondary">Anmelden</Link>
              <button 
                onClick={() => setShowSaveBanner(false)} 
                className="save-banner-close"
                aria-label="Banner schließen"
              >
                ✕
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Stats bar */}
      <div className="results-stats">
        <span className="stat">
          <strong>{stats?.total || 0}</strong> Immobilien geprüft
        </span>
        <span className="stat stat--pass">
          <strong>{stats?.matched || 0}</strong> Top-Treffer
        </span>
        <span className="stat stat--near">
          <strong>{stats?.nearMiss || 0}</strong> Beinahe-Treffer
        </span>
        <span className="stat stat--fail">
          <strong>{stats?.excluded || 0}</strong> ausgeschlossen
        </span>
        <div className="compare-btn-wrapper">
          <button
            className={`btn btn--compare ${compareIds.size >= 2 ? 'btn--compare-ready' : ''}`}
            onClick={() => {
              if (compareIds.size >= 2) {
                handleStartComparison();
              } else {
                setShowCompareInfo(prev => !prev);
              }
            }}
            disabled={isComparing}
          >
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="20" x2="18" y2="10" />
              <line x1="12" y1="20" x2="12" y2="4" />
              <line x1="6" y1="20" x2="6" y2="14" />
              <line x1="3" y1="20" x2="21" y2="20" />
            </svg>
            {isComparing ? 'Vergleiche...' : compareIds.size > 0 ? `Objekte vergleichen (${compareIds.size})` : 'Objekte vergleichen'}
          </button>
          {showCompareInfo && compareIds.size < 2 && (
            <div className="compare-info-popup">
              <button className="compare-info-close" onClick={() => setShowCompareInfo(false)}>&times;</button>
              <h4>
                <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="#8b5cf6" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="20" x2="18" y2="10" />
                  <line x1="12" y1="20" x2="12" y2="4" />
                  <line x1="6" y1="20" x2="6" y2="14" />
                  <line x1="3" y1="20" x2="21" y2="20" />
                </svg>
                Objekte vergleichen
              </h4>
              <p>Vergleichen Sie bis zu <strong>3 Immobilien</strong> direkt miteinander. Die KI erstellt eine detaillierte Analyse basierend auf Ihrem Lebensstil-Profil.</p>
              <div className="compare-info-steps">
                <div className="compare-info-step">
                  <span className="compare-info-step-num">1</span>
                  <span>Klicken Sie auf das <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="#8b5cf6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ verticalAlign: 'middle' }}><line x1="18" y1="20" x2="18" y2="10" /><line x1="12" y1="20" x2="12" y2="4" /><line x1="6" y1="20" x2="6" y2="14" /><line x1="3" y1="20" x2="21" y2="20" /></svg> Icon bei 2–3 Objekten</span>
                </div>
                <div className="compare-info-step">
                  <span className="compare-info-step-num">2</span>
                  <span>Klicken Sie hier auf «Objekte vergleichen»</span>
                </div>
                <div className="compare-info-step">
                  <span className="compare-info-step-num">3</span>
                  <span>Der Vergleich wird unter <strong>Mein Profil</strong> gespeichert</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Split view */}
      <div className="split-view">
        {/* Left: Listing cards */}
        <div className={`results-list ${activeTab === 'map' ? 'results-list--hidden-mobile' : ''}`}>
          {/* Commute overlap warning (inside sidebar) */}
          {commuteOverlap && !commuteOverlap.hasOverlap && commuteOverlap.warningMessage && (
            <div className="commute-overlap-warning">
              <div className="commute-overlap-warning-icon">
                <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                  <line x1="12" y1="9" x2="12" y2="13"/>
                  <line x1="12" y1="17" x2="12.01" y2="17"/>
                </svg>
              </div>
              <div className="commute-overlap-warning-text">
                <strong>Kein gemeinsamer Pendelbereich</strong>
                <p>{commuteOverlap.warningMessage}</p>
              </div>
            </div>
          )}

          {topMatches?.length > 0 && (
            <>
              <h2 className="results-section-title">Top-Treffer</h2>
              {topMatches.map((listing: any) => (
                <div
                  key={listing.id}
                  ref={(el) => { listingCardsRef.current[listing.id] = el; }}
                  className={highlightedListingId === listing.id ? 'listing-card-highlight' : ''}
                >
                  <ListingCard
                    listing={listing}
                    onClick={() => handleCardClick(listing.id)}
                    onDetailClick={() => navigate(`/listing/${listing.id}`)}
                    explanation={explanations.get(listing.id)}
                    onRequestExplanation={() => handleRequestExplanation(listing)}
                    isLoadingExplanation={loadingExplanations.has(listing.id)}
                    onMouseEnter={() => setHoveredListingId(listing.id)}
                    onMouseLeave={() => setHoveredListingId(null)}
                    isFavorite={favoriteIds.has(listing.id)}
                    onToggleFavorite={handleToggleFavorite}
                    isCompare={compareIds.has(listing.id)}
                    onToggleCompare={handleToggleCompare}
                    compareDisabled={compareIds.size >= 3}
                  />
                </div>
              ))}
            </>
          )}

          {nearMisses?.length > 0 && (
            <>
              <h2 className="results-section-title results-section-title--near">Beinahe-Treffer</h2>
              {nearMisses.map((listing: any) => (
                <div
                  key={listing.id}
                  ref={(el) => { listingCardsRef.current[listing.id] = el; }}
                  className={highlightedListingId === listing.id ? 'listing-card-highlight' : ''}
                >
                  <ListingCard
                    listing={listing}
                    onClick={() => handleCardClick(listing.id)}
                    onDetailClick={() => navigate(`/listing/${listing.id}`)}
                    explanation={explanations.get(listing.id)}
                    onRequestExplanation={() => handleRequestExplanation(listing)}
                    isLoadingExplanation={loadingExplanations.has(listing.id)}
                    onMouseEnter={() => setHoveredListingId(listing.id)}
                    onMouseLeave={() => setHoveredListingId(null)}
                    isFavorite={favoriteIds.has(listing.id)}
                    onToggleFavorite={handleToggleFavorite}
                    isCompare={compareIds.has(listing.id)}
                    onToggleCompare={handleToggleCompare}
                    compareDisabled={compareIds.size >= 3}
                  />
                </div>
              ))}
            </>
          )}

          {topMatches?.length === 0 && nearMisses?.length === 0 && (
            <div className="no-results">
              <h2>Keine Treffer gefunden</h2>
              <p>Versuchen Sie, Ihre Kriterien anzupassen.</p>
              <button className="btn btn--primary" onClick={() => navigate('/')}>
                Neue Suche starten
              </button>
            </div>
          )}
        </div>

        {/* Right: Map (full-screen behind everything) */}
        <div className={`results-map ${activeTab === 'list' ? 'results-map--hidden-mobile' : ''}`}>
          <PropertyMap listings={allListings} hoveredListingId={hoveredListingId} focusedListingId={focusedListingId} onMarkerClick={handleMarkerClick} onPopupClose={handlePopupClose} commuteZones={commuteOverlap?.workZones || undefined} commuteLabel={commuteOverlap?.commuteLabel} customPois={customPois} />
        </div>
      </div>

      {/* Mobile tab switcher (floating pill) */}
      <div className="mobile-tabs">
        <button
          className={`tab-btn ${activeTab === 'list' ? 'tab-btn--active' : ''}`}
          onClick={() => setActiveTab('list')}
        >
          Liste
        </button>
        <button
          className={`tab-btn ${activeTab === 'map' ? 'tab-btn--active' : ''}`}
          onClick={() => setActiveTab('map')}
        >
          Karte
        </button>
      </div>

      {/* Comparison Modal */}
      {showComparisonModal && (
        <div className="comparison-modal-overlay" onClick={() => !isComparing && setShowComparisonModal(false)}>
          <div className="comparison-modal" onClick={(e) => e.stopPropagation()}>
            <div className="comparison-modal-header">
              <h2>
                <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="20" x2="18" y2="10" />
                  <line x1="12" y1="20" x2="12" y2="4" />
                  <line x1="6" y1="20" x2="6" y2="14" />
                  <line x1="3" y1="20" x2="21" y2="20" />
                </svg>
                Objekte vergleichen
              </h2>
              <button
                className="comparison-modal-close"
                onClick={() => setShowComparisonModal(false)}
                disabled={isComparing}
                aria-label="Schliessen"
              >
                &times;
              </button>
            </div>
            <div className="comparison-modal-body">
              {isComparing && (
                <div className="comparison-loading">
                  <div className="spinner" />
                  <p>KI-Vergleich wird generiert... Dies kann einige Sekunden dauern.</p>
                </div>
              )}
              {comparisonResult && !isComparing && (
                <>
                  <div className="comparison-property-links">
                    {allListings.filter(l => compareIds.has(l.id)).map(l => (
                      <a
                        key={l.id}
                        className="comparison-property-link"
                        href={`/listing/${l.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
                        {l.title || `Objekt ${l.id}`}
                      </a>
                    ))}
                  </div>
                  <div className="comparison-result" dangerouslySetInnerHTML={{ __html: renderMarkdown(comparisonResult) }} />
                </>
              )}
            </div>
            {comparisonResult && !isComparing && (
              <div className="comparison-modal-footer">
                <button className="btn btn--secondary" onClick={() => setShowComparisonModal(false)}>
                  Schliessen
                </button>
                <button className="btn btn--primary" onClick={() => {
                  setShowComparisonModal(false);
                  setCompareIds(new Set());
                }}>
                  Fertig &amp; Auswahl aufheben
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/** Markdown to HTML converter for comparison text – tight, gap-free rendering */
function renderMarkdown(md: string): string {
  // Normalize line endings
  let html = md.replace(/\r\n/g, '\n');

  // Pre-process: move orphan colons inside bold markers  **text**: → **text:**
  html = html.replace(/\*\*([^*]+?)\*\*\s*:/g, '**$1:**');
  // Pre-process: remove blank lines between #### headers and their content
  html = html.replace(/(^#{1,4} .+)\n\n(?=- )/gm, '$1\n');

  // Tables: process before other transforms
  html = html.replace(/(?:^\|.+\|$\n?)+/gm, (block) => {
    const rows = block.trim().split('\n').filter(r => r.trim());
    const htmlRows = rows
      .filter(row => !/^[\s|\-:]+$/.test(row)) // skip separator rows like |---|---|---|
      .map(row => {
        const cells = row.split('|').filter(c => c !== '').map(c => c.trim());
        return '<tr>' + cells.map(c => `<td>${c}</td>`).join('') + '</tr>';
      })
      .join('');
    return `<table class="comparison-table">${htmlRows}</table>\n`;
  });

  html = html
    // Headers (order matters: longest prefix first)
    .replace(/^#### (.+)$/gm, '<h5>$1</h5>')
    .replace(/^### (.+)$/gm, '<h4>$1</h4>')
    .replace(/^## (.+)$/gm, '<h3>$1</h3>')
    .replace(/^# (.+)$/gm, '<h2>$1</h2>')
    // Bold
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    // Italic (but not inside words/paths)
    .replace(/(?<!\w)\*([^*]+?)\*(?!\w)/g, '<em>$1</em>')
    // Unordered lists
    .replace(/^- (.+)$/gm, '<li>$1</li>')
    .replace(/((?:<li>.*<\/li>\n?)+)/g, '<ul>$1</ul>')
    // Horizontal rules
    .replace(/^---+$/gm, '<hr />')
    // Remove excessive blank lines (3+ newlines → 2)
    .replace(/\n{3,}/g, '\n\n')
    // Paragraphs: wrap non-tag lines separated by double newlines
    .replace(/\n\n/g, '</p><p>')
    .replace(/\n/g, '<br />')
    // Wrap
    .replace(/^/, '<p>')
    .replace(/$/, '</p>');

  // Clean up: remove empty tags, fix tag nesting
  html = html
    .replace(/<p>\s*<\/p>/g, '')
    .replace(/<p><br \/>/g, '<p>')
    .replace(/<br \/><\/p>/g, '</p>')
    .replace(/<p>(<h[2345]>)/g, '$1')
    .replace(/(<\/h[2345]>)<\/p>/g, '$1')
    .replace(/<p>(<ul>)/g, '$1')
    .replace(/(<\/ul>)<\/p>/g, '$1')
    .replace(/<p>(<table)/g, '$1')
    .replace(/(<\/table>)<\/p>/g, '$1')
    .replace(/<p>(<hr \/>)/g, '$1')
    .replace(/(<hr \/>)<\/p>/g, '$1')
    // Remove double <br> between block elements
    .replace(/(<\/h[2345]>)<br \/>/g, '$1')
    .replace(/<br \/>(<h[2345]>)/g, '$1')
    .replace(/(<\/ul>)<br \/>/g, '$1')
    .replace(/<br \/>(<ul>)/g, '$1')
    .replace(/(<\/table>)<br \/>/g, '$1')
    .replace(/<br \/>(<table)/g, '$1')
    .replace(/(<hr \/>)<br \/>/g, '$1')
    .replace(/<br \/>(<hr \/>)/g, '$1')
    // Collapse gap between bold label paragraph and following list/content
    .replace(/<\/strong><\/p>\s*<p>/g, '</strong><br />')
    .replace(/<\/strong><br \/><br \/>/g, '</strong><br />')
    // Remove orphan <br/> right before <ul> or <li>
    .replace(/<br \/>\s*(<ul>)/g, '$1')
    .replace(/<br \/>\s*(<li>)/g, '$1')
    // Remove <br/> right after closing </ul> before next content
    .replace(/(<\/ul>)\s*<br \/>/g, '$1')
    // CRITICAL: Merge adjacent <ul> blocks separated by gaps (</ul> + whitespace/br/p + <ul>)
    // This happens when AI puts a blank line between a bold label bullet and sub-items
    .replace(/<\/ul>\s*<p>\s*<\/p>\s*<ul>/g, '')
    .replace(/<\/ul>\s*<br \/>\s*<ul>/g, '')
    .replace(/<\/ul>\s*<\/p>\s*<p>\s*<ul>/g, '')
    .replace(/<\/ul>\s*<ul>/g, '')
    // Remove <br> between consecutive </li><li> within merged lists
    .replace(/<\/li>\s*<br \/>\s*<li>/g, '</li><li>')
    // Fix orphan colons: </strong><br />: or </strong>\n: → </strong>:
    .replace(/<\/strong>\s*<br \/>\s*:/g, '</strong>:')
    .replace(/<\/strong>\s*:\s*<br \/>/g, '</strong>: ')
    // Remove lone ":" on its own line (orphan from AI formatting)
    .replace(/<br \/>\s*:\s*<br \/>/g, '<br />')
    .replace(/<li>\s*:\s*<\/li>/g, '')
    .replace(/<p>\s*:\s*<\/p>/g, '');

  return html;
}
